<template>
	<app-button theme="outline" :disabled="loading" @click="open = true">{{
		resourceLabel
	}}</app-button>

	<slideover :title="resourceLabel" :open="open" class="absolute" @close="open = false">
		<list-group v-if="resource" :items="resourceDetails" compact />
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, computed } from 'vue'
	import { Contract } from '@/data/schema/contract'
	import { Collection } from '@/data/schema/collection'
	import { Journey } from '@/data/schema/journey'
	import { formatDate } from '@/utils/date'

	const props = withDefaults(
		defineProps<{
			resource?: Contract | Collection | Journey
			loading?: boolean
		}>(),
		{
			resource: undefined,
			loading: false
		}
	)

	const open = ref(false)

	const resourceLabel = computed(() => {
		if (props.resource?.object === 'JourneyHeader') {
			return 'Journey Details'
		}

		if (props.resource?.object === 'Collection') {
			return 'Collection Details'
		}

		if (props.resource) {
			return 'Contract Details'
		}

		return 'Details'
	})

	// Return contract details object for panel
	const resourceDetails = computed(() => {
		if (props.resource?.object === 'Collection') {
			const resource = props.resource as Collection
			return {
				collection_no: resource?.collection_number,
				contract_no: resource?.contract_number,
				account_no: resource?.account,
				account_name: resource?.account_name,
				site_address: formattedAddress.value,
				site_phone: resource?.delivery_telephone
			}
		}

		if (props.resource?.object === 'JourneyHeader') {
			const resource = props.resource as Journey
			return {
				link_no: resource?.link_number,
				journey_no: resource?.journey_number,
				vehicle_code: resource?.vehicle_display_name,
				vehicle_name: resource?.vehicle_name,
				type:
					resource?.type == 'DOF'
						? 'TOS'
						: resource?.type == 'COL'
							? 'Collection'
							: 'Delivery',
				date: formatDate(resource?.delivery_date),
				time: resource?.delivery_time,
				account_no: resource?.account_number,
				account_name: resource?.account_name,
				address: resource?.delivery_address,
				site_contact_name: resource?.site_contact_name,
				site_contact_telephone: resource?.site_contact_telephone
			}
		}

		const resource = props.resource as Contract

		return {
			contract_no: resource?.contract_number,
			account_no: resource?.account,
			account_name: resource?.account_name,
			site_address: formattedAddress.value,
			site_contact: resource?.delivery_contact,
			site_phone: resource?.delivery_number,
			site_email: resource?.delivery_email,
			ordered_by: resource?.ordered_by,
			ordered_by_phone: resource?.ordered_by_number,
			ordered_by_email: resource?.ordered_by_email,
			sales_lead: resource?.sales_lead?.name
		}
	})

	// Return formatted address from Contract
	const formattedAddress = computed(() => {
		const con = props.resource as Contract

		if (con) {
			const address = [
				con.delivery_address_1,
				con.delivery_address_2,
				con.delivery_address_3,
				con.delivery_address_4,
				con.delivery_address_5,
				con.delivery_postcode
			]

			return address.filter((addressLine) => addressLine).join('<br>')
		}

		return ''
	})
</script>
